import { For, Show } from "solid-js";
import type { InterestTag, SkillTag } from "~/lib/api";
import {
  LOOKING_FOR_MAX_LEN,
  LOOKING_FOR_SUGGESTIONS,
  lookingForHasPhrase,
  toggleLookingForSuggestion,
} from "~/lib/looking-for";
import { SKILL_CATEGORY_LABELS, skillsByCategory } from "~/lib/skills";

export type ProfileFormValues = {
  displayName: string;
  headline: string;
  bio: string;
  company: string;
  jobRole: string;
  lookingFor: string;
  selectedInterests: string[];
  selectedSkills?: string[];
};

type Props = {
  values: ProfileFormValues;
  interests?: InterestTag[];
  skills?: SkillTag[];
  showInterests?: boolean;
  showSkills?: boolean;
  error?: string;
  pending?: boolean;
  submitLabel?: string;
  onChange: (patch: Partial<ProfileFormValues>) => void;
  onToggleLookingFor?: (phrase: string) => void;
  onToggleInterest: (id: string) => void;
  onToggleSkill?: (id: string) => void;
  onSubmit: () => void;
  onCancel?: () => void;
};

export function ProfileForm(props: Props) {
  return (
    <form
      class="wizard-form"
      onSubmit={(e) => {
        e.preventDefault();
        props.onSubmit();
      }}
    >
      <label class="field">
        <span>Display name</span>
        <input
          required
          value={props.values.displayName}
          onInput={(e) => props.onChange({ displayName: e.currentTarget.value })}
        />
      </label>
      <label class="field">
        <span>Headline</span>
        <input
          value={props.values.headline}
          onInput={(e) => props.onChange({ headline: e.currentTarget.value })}
        />
      </label>
      <label class="field">
        <span>Company</span>
        <input
          value={props.values.company}
          onInput={(e) => props.onChange({ company: e.currentTarget.value })}
        />
      </label>
      <label class="field">
        <span>Role</span>
        <input
          value={props.values.jobRole}
          onInput={(e) => props.onChange({ jobRole: e.currentTarget.value })}
        />
      </label>
      <label class="field">
        <span>Bio</span>
        <textarea
          rows={4}
          value={props.values.bio}
          onInput={(e) => props.onChange({ bio: e.currentTarget.value })}
        />
      </label>
      <label class="field">
        <span>Looking for</span>
        <input
          value={props.values.lookingFor}
          maxLength={LOOKING_FOR_MAX_LEN}
          onInput={(e) => props.onChange({ lookingFor: e.currentTarget.value })}
          placeholder="e.g. evaluating Flink, architecture chats"
        />
        <p class="field-hint">Free text on your profile. Hiring, roles, mentoring, and speaking use Open to above.</p>
      </label>
      <div class="looking-for-suggestions">
        <p class="muted">Tap to add</p>
        <div class="interest-grid">
          <For each={LOOKING_FOR_SUGGESTIONS}>
            {(suggestion) => (
              <button
                type="button"
                class={`interest-chip${lookingForHasPhrase(props.values.lookingFor, suggestion.text) ? " selected" : ""}`}
                aria-pressed={lookingForHasPhrase(props.values.lookingFor, suggestion.text)}
                onClick={() => {
                  if (props.onToggleLookingFor) {
                    props.onToggleLookingFor(suggestion.text);
                    return;
                  }
                  props.onChange({
                    lookingFor: toggleLookingForSuggestion(props.values.lookingFor, suggestion.text),
                  });
                }}
              >
                {suggestion.label}
              </button>
            )}
          </For>
        </div>
      </div>

      <Show when={props.showSkills && props.skills && props.onToggleSkill}>
        <div>
          <p class="status-chip">Skills</p>
          <p class="field-hint">
            Up to 15
            <Show when={(props.values.selectedSkills ?? []).length}>
              {" "}
              — {(props.values.selectedSkills ?? []).length} selected
            </Show>
          </p>
          <For each={skillsByCategory(props.skills!)}>
            {([category, tags]) => (
              <div class="skill-category">
                <p class="skill-category-label">{SKILL_CATEGORY_LABELS[category] ?? category}</p>
                <div class="interest-grid" style={{ margin: "0" }}>
                  <For each={tags}>
                    {(tag) => (
                      <button
                        type="button"
                        class={`skill-chip skill-chip--toggle${(props.values.selectedSkills ?? []).includes(tag.id) ? " selected" : ""}`}
                        aria-pressed={(props.values.selectedSkills ?? []).includes(tag.id)}
                        onClick={() => props.onToggleSkill?.(tag.id)}
                      >
                        {tag.label}
                      </button>
                    )}
                  </For>
                </div>
              </div>
            )}
          </For>
        </div>
      </Show>

      <Show when={props.showInterests && props.interests}>
        <div>
          <p class="status-chip">Interests</p>
          <p class="field-hint">
            Up to 10
            <Show when={props.values.selectedInterests.length}>
              {" "}
              — {props.values.selectedInterests.length} selected
            </Show>
          </p>
          <div class="interest-grid" style={{ margin: "0" }}>
            <For each={props.interests!}>
              {(tag) => (
                <button
                  type="button"
                  class={`interest-chip${props.values.selectedInterests.includes(tag.id) ? " selected" : ""}`}
                  onClick={() => props.onToggleInterest(tag.id)}
                >
                  {tag.label}
                </button>
              )}
            </For>
          </div>
        </div>
      </Show>

      <Show when={props.error}>
        <div class="error-box">{props.error}</div>
      </Show>

      <div class="wizard-actions">
        <Show when={props.onCancel}>
          <button type="button" class="btn btn-ghost" onClick={() => props.onCancel?.()}>
            Cancel
          </button>
        </Show>
        <button type="submit" class="btn btn-primary" disabled={props.pending || !props.values.displayName.trim()}>
          {props.submitLabel ?? "Save profile"}
        </button>
      </div>
    </form>
  );
}